import type { IntellioAssistantConversationSessionRecord } from '@triedge/domain-core';
import { resolveAssistantRouteFamily } from '../../config/ai-assistant/route-family-registry';
import { resolveAIAssistantModule } from './aiModuleConfig';

const normalizePathname = (value: string | null | undefined): string =>
  String(value ?? '')
    .trim()
    .split('?')[0]
    .replace(/\/+$/, '')
    .toLowerCase();

export const resolveConversationRouteScope = (pathname: string): string | null => {
  const normalizedPathname = normalizePathname(pathname);

  const financialAidDetailMatch = normalizedPathname.match(/^\/school\/financial(?:aid|-aid)\/staff\/([^/]+)/);
  if (financialAidDetailMatch?.[1]) {
    return `financial-aid-detail:${financialAidDetailMatch[1]}`;
  }

  const admissionsDetailMatch = normalizedPathname.match(/^\/school\/admissions\/(?:children|family|documents|notes|tuition)\/([^/]+)/);
  if (admissionsDetailMatch?.[1]) {
    return `admissions-detail:${admissionsDetailMatch[1]}`;
  }

  const studentsFamilyMatch = normalizedPathname.match(/^\/school\/students\/family\/([^/]+)/);
  if (studentsFamilyMatch?.[1]) {
    return `students-family-detail:${studentsFamilyMatch[1]}`;
  }

  return null;
};

export const isStudentsFamiliesFamilyDetailRoute = (
  pathname: string | null | undefined,
): boolean => (
  /^\/school\/students\/family\/[^/]+(?:\/(?:children|family|tuition|documents|notes))?$/i
    .test(normalizePathname(pathname))
);

export const doesConversationSessionMatchRoute = (
  session: Pick<IntellioAssistantConversationSessionRecord, 'route'>,
  pathname: string,
  moduleId: string,
): boolean => {
  const sessionRoute = String(session.route ?? '').trim();
  if (!sessionRoute) {
    return false;
  }
  const sessionScope = resolveConversationRouteScope(sessionRoute);
  const pathnameScope = resolveConversationRouteScope(pathname);
  if (sessionScope || pathnameScope) {
    return sessionScope === pathnameScope;
  }
  return (
    resolveAIAssistantModule(sessionRoute) === moduleId
    && resolveAssistantRouteFamily(sessionRoute).id === resolveAssistantRouteFamily(pathname).id
  );
};

export const selectPreferredConversationSessionId = (input: {
  sessions: IntellioAssistantConversationSessionRecord[];
  currentConversationSessionId: string | null;
  pathname: string;
  moduleId: string;
  preferFreshEmptySession?: boolean;
}): string | null => {
  const matchingOpenSessions = input.sessions.filter((session) => (
    session.status === 'open'
    && doesConversationSessionMatchRoute(session, input.pathname, input.moduleId)
  ));
  const matchingOpenSessionsWithMessages = matchingOpenSessions.filter((session) => session.messageCount > 0);
  const currentSession = input.currentConversationSessionId
    ? input.sessions.find((session) => session.id === input.currentConversationSessionId) ?? null
    : null;
  if (
    currentSession
    && doesConversationSessionMatchRoute(currentSession, input.pathname, input.moduleId)
    && (
      input.preferFreshEmptySession
      || currentSession.messageCount > 0
      || matchingOpenSessionsWithMessages.length === 0
      || matchingOpenSessionsWithMessages[0]?.id === currentSession.id
    )
  ) {
    return currentSession.id;
  }
  if (input.preferFreshEmptySession) {
    return matchingOpenSessions.find((session) => session.messageCount === 0)?.id ?? null;
  }
  return matchingOpenSessionsWithMessages[0]?.id
    ?? matchingOpenSessions[0]?.id
    ?? null;
};

export type PendingFreshConversationSessionGuard = {
  epoch: number;
  pendingFreshSessionId: string | null;
};

export const selectConversationSessionIdWithFreshSessionGuard = (input: {
  sessions: IntellioAssistantConversationSessionRecord[];
  currentConversationSessionId: string | null;
  pathname: string;
  moduleId: string;
  preferFreshEmptySession?: boolean;
  pendingFreshSessionGuard?: PendingFreshConversationSessionGuard | null;
}): string | null => {
  const guard = input.pendingFreshSessionGuard ?? null;
  if (guard) {
    return guard.pendingFreshSessionId;
  }

  return selectPreferredConversationSessionId({
    sessions: input.sessions,
    currentConversationSessionId: input.currentConversationSessionId,
    pathname: input.pathname,
    moduleId: input.moduleId,
    preferFreshEmptySession: input.preferFreshEmptySession,
  });
};

export const shouldHydrateConversationTimelineForFreshSessionGuard = (input: {
  sessionId: string;
  pendingFreshSessionGuard?: PendingFreshConversationSessionGuard | null;
}): boolean => {
  const guard = input.pendingFreshSessionGuard ?? null;
  if (!guard) {
    return true;
  }
  return guard.pendingFreshSessionId === input.sessionId;
};
